import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Shell, { Breadcrumbs } from "@/components/Shell";
import { Card, Btn, Input, Select, Label, Spinner } from "@/components/ui-kit";
import api from "@/lib/api";
import { formatApiError, QUOTE_STATUSES, toISODate } from "@/lib/format";

const QuoteForm = ({ mode = "new" }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [form, setForm] = useState({
    client_id: "",
    contact_id: "",
    name: "",
    date: new Date().toISOString().slice(0, 10),
    status: "Draft",
  });
  const [loading, setLoading] = useState(mode === "edit");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data));
    if (mode === "edit") {
      api.get(`/quotes/${id}`).then((r) => {
        setForm({
          client_id: r.data.client_id,
          contact_id: r.data.contact_id || "",
          name: r.data.name || "",
          date: r.data.date || "",
          status: r.data.status || "Draft",
        });
      }).finally(() => setLoading(false));
    }
  }, [id, mode]);

  useEffect(() => {
    if (!form.client_id) { setContacts([]); return; }
    api.get(`/clients/${form.client_id}`).then((r) => setContacts(r.data.contacts || []));
  }, [form.client_id]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const payload = { ...form, date: toISODate(form.date) };
      if (mode === "new") {
        const { data } = await api.post("/quotes", payload);
        navigate(`/quotes/${data.id}`);
      } else {
        await api.put(`/quotes/${id}`, payload);
        navigate(`/quotes/${id}`);
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Shell><div className="flex justify-center py-20"><Spinner /></div></Shell>;

  return (
    <Shell>
      <Breadcrumbs items={[{ to: "/quotes", label: "Quotes" }, { label: mode === "new" ? "New Quote" : "Edit Quote" }]} />
      <h1 className="font-display text-4xl font-bold text-zinc-950 mb-1">{mode === "new" ? "New Quote" : "Edit Quote"}</h1>
      <p className="text-sm text-zinc-500 mb-8">Quote Details</p>

      <Card className="p-6 max-w-2xl">
        <form onSubmit={submit} className="space-y-4" data-testid="quote-form">
          <div>
            <Label required>Client</Label>
            <Select required data-testid="quote-client-select" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value, contact_id: "" })}>
              <option value="">Select client…</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </div>
          <div>
            <Label>Contact (FAO)</Label>
            <Select data-testid="quote-contact-select" value={form.contact_id} onChange={(e) => setForm({ ...form, contact_id: e.target.value })} disabled={!contacts.length}>
              <option value="">{contacts.length === 0 ? "No contacts for this client" : "Select contact…"}</option>
              {contacts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </div>
          <div>
            <Label required>Quote Name</Label>
            <Input required data-testid="quote-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Bowood Hotel – A7 Tent Cards April 2026" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Quote Date</Label>
              <Input type="date" data-testid="quote-date-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div>
              <Label required>Status</Label>
              <Select data-testid="quote-status-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                {QUOTE_STATUSES.map((s) => <option key={s}>{s}</option>)}
              </Select>
            </div>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex gap-2 pt-2">
            <Btn type="submit" disabled={saving} data-testid="quote-save-btn">{mode === "new" ? "Create Quote" : "Update Quote"}</Btn>
            <Btn type="button" variant="secondary" onClick={() => navigate(-1)} data-testid="quote-cancel-btn">Cancel</Btn>
          </div>
          {mode === "new" && (
            <p className="text-xs text-zinc-500 pt-2">A quote number (Q-prefix) will be auto-assigned. Add tasks and supplier PDFs after creating.</p>
          )}
        </form>
      </Card>
    </Shell>
  );
};

export default QuoteForm;
